import { useEffect, useState } from "react";
import { fetchClients, addClient, updateClient, deleteClient, rotateAgentKey, ApiError } from "../api/client.js";
import type { ClientView } from "@atlas/shared/types";

interface AddFormState {
  name: string;
  slug: string;
  slackWebhook: string;
}

interface EditFormState {
  name: string;
  slackWebhook: string;
}

const EMPTY_ADD_FORM: AddFormState = { name: "", slug: "", slackWebhook: "" };

function slugify(name: string): string {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
}

export default function ClientsPage() {
  const [clients, setClients] = useState<ClientView[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<AddFormState>(EMPTY_ADD_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  // Key rotation state
  const [rotatingKeyId, setRotatingKeyId] = useState<number | null>(null);
  const [newKeys, setNewKeys] = useState<Record<number, string>>({});
  const [copied, setCopied] = useState<number | null>(null);

  // Editing state — keyed by client id
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editForm, setEditForm] = useState<EditFormState>({ name: "", slackWebhook: "" });
  const [editError, setEditError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);

  function load() {
    setLoading(true);
    fetchClients()
      .then(setClients)
      .catch((e: unknown) => setError(e instanceof ApiError ? e.message : "Failed to load clients"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  function handleNameChange(name: string) {
    setForm((f) => ({
      ...f,
      name,
      slug: f.slug === slugify(f.name) || f.slug === "" ? slugify(name) : f.slug,
    }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setFormError(null);
    setSubmitting(true);
    try {
      const slackWebhook = form.slackWebhook.trim() || undefined;
      await addClient({
        name: form.name.trim(),
        slug: form.slug.trim(),
        ...(slackWebhook ? { slackWebhook } : {}),
      });
      setForm(EMPTY_ADD_FORM);
      setShowForm(false);
      load();
    } catch (e) {
      setFormError(e instanceof ApiError ? e.message : "Failed to add client");
    } finally {
      setSubmitting(false);
    }
  }

  function startEdit(cl: ClientView) {
    setEditingId(cl.id);
    setEditForm({ name: cl.name, slackWebhook: cl.slackWebhook ?? "" });
    setEditError(null);
  }

  function cancelEdit() {
    setEditingId(null);
    setEditError(null);
  }

  async function handleSave(id: number) {
    setSaving(true);
    setEditError(null);
    try {
      const slackWebhook = editForm.slackWebhook.trim() || undefined;
      await updateClient(id, {
        name: editForm.name.trim(),
        ...(slackWebhook ? { slackWebhook } : {}),
      });
      setEditingId(null);
      load();
    } catch (e) {
      setEditError(e instanceof ApiError ? e.message : "Failed to update client");
    } finally {
      setSaving(false);
    }
  }

  async function handleRotateKey(cl: ClientView) {
    const action = cl.hasAgentKey ? "rotate" : "generate";
    if (cl.hasAgentKey && !window.confirm(`Rotate the agent API key for "${cl.name}"? The current key will stop working immediately.`)) return;
    setRotatingKeyId(cl.id);
    try {
      const { apiKey } = await rotateAgentKey(cl.id);
      setNewKeys((k) => ({ ...k, [cl.id]: apiKey }));
      load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : `Failed to ${action} key`);
    } finally {
      setRotatingKeyId(null);
    }
  }

  async function handleCopy(id: number, key: string) {
    await navigator.clipboard.writeText(key);
    setCopied(id);
    setTimeout(() => setCopied((c) => (c === id ? null : c)), 2000);
  }

  async function handleDelete(cl: ClientView) {
    if (!window.confirm(`Delete "${cl.name}"? This will permanently remove all associated domains and scan history.`)) return;
    setDeletingId(cl.id);
    try {
      await deleteClient(cl.id);
      load();
    } catch (e) {
      alert(e instanceof ApiError ? e.message : "Failed to delete client");
    } finally {
      setDeletingId(null);
    }
  }

  if (loading) return <div className="text-gray-500 text-sm">Loading…</div>;
  if (error) return <div className="text-red-400 text-sm">Error: {error}</div>;

  return (
    <div className="space-y-4 max-w-2xl">
      <div className="flex items-center justify-between">
        <h1 className="text-xl font-semibold">Clients</h1>
        <button
          onClick={() => { setShowForm((v) => !v); setFormError(null); }}
          className="text-sm px-3 py-1.5 rounded bg-gray-800 hover:bg-gray-700 text-white transition-colors"
        >
          {showForm ? "Cancel" : "+ New Client"}
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={handleSubmit}
          className="border border-gray-700 rounded-lg p-4 space-y-3 bg-gray-900"
        >
          <div className="text-sm font-medium text-gray-300">Add Client</div>

          <div className="space-y-1">
            <label className="text-xs text-gray-500">Name</label>
            <input
              required
              value={form.name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="Acme Corp"
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-500"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-500">Slug <span className="text-gray-600">(lowercase, hyphens only)</span></label>
            <input
              required
              value={form.slug}
              onChange={(e) => setForm((f) => ({ ...f, slug: e.target.value }))}
              placeholder="acme-corp"
              pattern="[a-z0-9-]+"
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-500 font-mono"
            />
          </div>

          <div className="space-y-1">
            <label className="text-xs text-gray-500">Slack Webhook <span className="text-gray-600">(optional)</span></label>
            <input
              value={form.slackWebhook}
              onChange={(e) => setForm((f) => ({ ...f, slackWebhook: e.target.value }))}
              placeholder="https://hooks.slack.com/services/…"
              type="url"
              className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-500"
            />
          </div>

          {formError && <div className="text-red-400 text-xs">{formError}</div>}

          <button
            type="submit"
            disabled={submitting}
            className="px-4 py-1.5 rounded bg-blue-700 hover:bg-blue-600 disabled:opacity-50 text-sm text-white transition-colors"
          >
            {submitting ? "Adding…" : "Add Client"}
          </button>
        </form>
      )}

      <div className="grid gap-3">
        {clients.map((cl) => (
          <div key={cl.id} className="border border-gray-800 rounded-lg p-4 hover:border-gray-700 transition-colors">
            {editingId === cl.id ? (
              <div className="space-y-3">
                <div className="space-y-1">
                  <label className="text-xs text-gray-500">Name</label>
                  <input
                    value={editForm.name}
                    onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                    className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white focus:outline-none focus:border-gray-500"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-xs text-gray-500">Slack Webhook <span className="text-gray-600">(optional)</span></label>
                  <input
                    value={editForm.slackWebhook}
                    onChange={(e) => setEditForm((f) => ({ ...f, slackWebhook: e.target.value }))}
                    placeholder="https://hooks.slack.com/services/…"
                    type="url"
                    className="w-full bg-gray-800 border border-gray-700 rounded px-3 py-1.5 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-gray-500"
                  />
                </div>
                {editError && <div className="text-red-400 text-xs">{editError}</div>}
                <div className="flex gap-2">
                  <button
                    onClick={() => handleSave(cl.id)}
                    disabled={saving || !editForm.name.trim()}
                    className="px-3 py-1 rounded bg-blue-700 hover:bg-blue-600 disabled:opacity-50 text-xs text-white transition-colors"
                  >
                    {saving ? "Saving…" : "Save"}
                  </button>
                  <button
                    onClick={cancelEdit}
                    className="px-3 py-1 rounded bg-gray-700 hover:bg-gray-600 text-xs text-white transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="font-medium">{cl.name}</div>
                    <div className="text-xs text-gray-500 font-mono">{cl.slug}</div>
                    <div className="text-xs text-gray-600 font-mono">id: {cl.id}</div>
                  </div>
                  <div className="flex items-center gap-4">
                    <div className="text-right text-sm">
                      <div>
                        <span className="text-gray-400">{cl.domainCount}</span>
                        <span className="text-gray-600"> domains</span>
                      </div>
                      {cl.atRiskCount > 0 && (
                        <div className="text-amber-400 text-xs">{cl.atRiskCount} at risk</div>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => startEdit(cl)}
                        disabled={deletingId === cl.id}
                        className="text-xs text-gray-400 hover:text-white disabled:opacity-40 transition-colors"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(cl)}
                        disabled={deletingId === cl.id}
                        className="text-xs text-red-500 hover:text-red-400 disabled:opacity-40 transition-colors"
                      >
                        {deletingId === cl.id ? "Deleting…" : "Delete"}
                      </button>
                    </div>
                  </div>
                </div>

                {/* Agent key management */}
                <div className="border-t border-gray-800 pt-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-gray-500">Agent key</span>
                      {cl.hasAgentKey ? (
                        <span className="text-xs text-green-500">● configured</span>
                      ) : (
                        <span className="text-xs text-amber-500">● not set</span>
                      )}
                    </div>
                    <button
                      onClick={() => handleRotateKey(cl)}
                      disabled={rotatingKeyId === cl.id}
                      className="text-xs text-gray-400 hover:text-white disabled:opacity-40 transition-colors"
                    >
                      {rotatingKeyId === cl.id
                        ? "Generating…"
                        : cl.hasAgentKey
                        ? "Rotate key"
                        : "Generate key"}
                    </button>
                  </div>

                  {newKeys[cl.id] && (
                    <div className="mt-2 rounded bg-gray-950 border border-amber-800 p-3 space-y-2">
                      <div className="text-xs text-amber-400 font-medium">
                        Copy this key now — it will not be shown again
                      </div>
                      <div className="flex items-center gap-2">
                        <code className="flex-1 text-xs text-green-400 font-mono break-all">
                          {newKeys[cl.id]}
                        </code>
                        <button
                          onClick={() => handleCopy(cl.id, newKeys[cl.id]!)}
                          className="shrink-0 text-xs px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors"
                        >
                          {copied === cl.id ? "Copied!" : "Copy"}
                        </button>
                      </div>
                      <div className="text-xs text-gray-500">
                        Set <code className="text-gray-400">atlas.api_key</code> and{" "}
                        <code className="text-gray-400">client_id: {cl.id}</code> in your{" "}
                        <code className="text-gray-400">atlas-agent.yaml</code>, then restart the agent.
                      </div>
                      <div className="border-t border-gray-800 pt-2 space-y-1">
                        <div className="text-xs text-gray-500 mb-1.5">Download agent (Windows)</div>
                        <div className="flex gap-2">
                          <a
                            href="/downloads/atlas-agent-windows-amd64-dev.zip"
                            download
                            className="text-xs px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors"
                          >
                            ↓ amd64 (Intel/AMD)
                          </a>
                          <a
                            href="/downloads/atlas-agent-windows-arm64-dev.zip"
                            download
                            className="text-xs px-2 py-1 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 transition-colors"
                          >
                            ↓ arm64
                          </a>
                        </div>
                      </div>
                      <button
                        onClick={() => setNewKeys((k) => { const n = { ...k }; delete n[cl.id]; return n; })}
                        className="text-xs text-gray-600 hover:text-gray-400 transition-colors"
                      >
                        Dismiss
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}
        {clients.length === 0 && !showForm && (
          <div className="text-center text-gray-600 py-8">
            No clients yet — add one above.
          </div>
        )}
      </div>
    </div>
  );
}
